import React, { ChangeEvent, Fragment, ReactElement, useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Box, Backdrop, CircularProgress, TextField, Button, Typography, Grid2 } from '@mui/material';
import { RootState } from '../../redux/reducers';
import { importClients } from '../../redux/actions/clientActions';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import ClientsTable from './ClientsTable';
import ClientForm from './forms/ClientForm';
import { useLocation } from 'react-router-dom';
import { ClientType } from '../../api/ClientApi';
import { getCustomers, getSuppliers } from '../../redux/selectors/clientsSelector';
import { isCustomer, isCustomerPage, isSupplier, isSupplierPage } from './ClientsHelper';
import { ClientTypeSR } from '../../common/Types';
import { DetailsContainerBox, DetailsTableContainerBox } from '../../common/ResponsiveStyle';
import {
    isEmployeeProcurementManager,
    isEmployeeProjectAdmin,
    isEmployeeProjectManager,
} from '../../redux/selectors/employeeSelector';

export default function ClientsList(): ReactElement {
    const clients = useAppSelector((state: RootState) => state.clients.clients);
    const customers = useAppSelector(getCustomers);
    const suppliers = useAppSelector(getSuppliers);

    const isProjectManager = useAppSelector(isEmployeeProjectManager);
    const isProjectAdmin = useAppSelector(isEmployeeProjectAdmin);
    const isProcurementManager = useAppSelector(isEmployeeProcurementManager);

    const [filter, setFilter] = React.useState<string>('');

    const dispatch = useAppDispatch();

    const location = useLocation();
    const path = location.pathname;

    const pageClientType = useMemo(() => {
        return isSupplierPage(path) ? ClientType.Supplier : isCustomerPage(path) ? ClientType.Customer : undefined;
    }, [path]);

    useEffect(() => {
        if (!clients) {
            dispatch(importClients());
        }
    }, [dispatch]);

    const handleFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFilter(value);
    };

    const [openClientForm, setOpenClientForm] = useState<boolean>(false);

    const goToForm = () => {
        setOpenClientForm(true);
    };

    const handleCloseClientForm = () => {
        setOpenClientForm(false);
    };

    const currClients = isCustomer(pageClientType) ? customers : isSupplier(pageClientType) ? suppliers : undefined;

    const filteredClients = currClients?.filter(
        (c) =>
            c.address?.toLowerCase().includes(filter.toLowerCase()) ||
            c.name?.toLowerCase().includes(filter.toLowerCase()) ||
            c.shortName?.toLowerCase().includes(filter.toLowerCase()),
    );

    return clients === null ? (
        <Fragment>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={!Boolean(clients)}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Fragment>
    ) : (
        <DetailsContainerBox
            sx={{
                p: 2,
                backgroundColor: (theme) => (theme.palette.mode === 'light' ? 'white' : theme.palette.grey[900]),
            }}
        >
            <Grid2 container sx={{ py: 2, px: { xs: 2, sm: 4 }, justifyContent: 'space-between', gap: 2 }}>
                <Grid2>
                    <Typography variant="h5">{pageClientType ? ClientTypeSR[pageClientType] + 'i' : ''}</Typography>
                </Grid2>
                {(((isProjectManager || isProjectAdmin) && pageClientType === ClientType.Customer) ||
                    ((isProjectAdmin || isProcurementManager) && pageClientType === ClientType.Supplier)) && (
                    <Grid2>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={goToForm}
                            startIcon={<AddIcon />}
                            sx={{ textTransform: 'none' }}
                        >
                            Novi {pageClientType ? ClientTypeSR[pageClientType].toLowerCase() : ''}
                        </Button>
                    </Grid2>
                )}
            </Grid2>
            <hr />
            <Box sx={{ p: { xs: 1, sm: 4 }, pt: 2 }}>
                <TextField
                    size="small"
                    sx={{ mb: 1, width: '100%' }}
                    placeholder="Filter"
                    value={filter}
                    onChange={handleFilterChange}
                />
                <Box sx={{ p: 0, mt: 2 }}></Box>

                <DetailsTableContainerBox>
                    <ClientsTable clients={filteredClients ?? null} />
                </DetailsTableContainerBox>
            </Box>
            <ClientForm closeForm={handleCloseClientForm} open={openClientForm} clientId={undefined} />
        </DetailsContainerBox>
    );
}
